import React, { useEffect, useState } from "react";
import { useGame } from "../game/em2c-store";
import * as Audio from "../game/audio";
import { CheckCircle2, Circle, Rocket, ScanLine, Save, RotateCw, Trophy, Skull, Package, Volume2, VolumeX, FlaskConical, ChevronDown } from "lucide-react";

export default function Objectives() {
  const [open, setOpen] = useState(false);
  const [muted, setMutedState] = useState(false);
  const objectives = useGame((s) => s.objectives);
  const requestColonists = useGame((s) => s.requestColonists);
  const discoverDeposits = useGame((s) => s.discoverDeposits);
  const openCargo = useGame((s) => s.openCargo);
  const openResearch = useGame((s) => s.openResearch);
  const researchActive = useGame((s) => s.research.active);
  const researchCompleted = useGame((s) => s.research.completed);
  const cargoActive = useGame((s) => s.cargo.active);
  const save = useGame((s) => s.save);
  const load = useGame((s) => s.load);
  const reset = useGame((s) => s.reset);
  const victory = useGame((s) => s.victory);
  const defeat = useGame((s) => s.defeat);
  const survivedSols = useGame((s) => s.survivedSols);
  const colonists = useGame((s) => s.colonists.length);

  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("mars:world-click", close);
    const otherMenu = (e) => { if (e.detail?.name !== "objectives") close(); };
    window.addEventListener("mars:menu-open", otherMenu);
    return () => { window.removeEventListener("mars:world-click", close); window.removeEventListener("mars:menu-open", otherMenu); };
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMutedState(next);
    Audio.setMuted(next);
    if (next) Audio.stopAmbient();
    else Audio.startAmbient();
  };

  const done = objectives.filter((o) => o.done).length;

  return (
    <div
      className="fixed top-14 right-[7.25rem] z-40 pointer-events-auto"
      style={{ fontFamily: "Rajdhani, sans-serif" }}
      data-testid="objectives-anchor"
    >
      <button
        onClick={() => { if (!open) window.dispatchEvent(new CustomEvent("mars:menu-open", { detail: { name: "objectives" } })); setOpen(value => !value); }}
        className="h-8 min-w-[9.4rem] flex items-center justify-center gap-2 px-3 border border-white/15 bg-[#0A0C10]/70 backdrop-blur-md hover:bg-cyan-500/10 transition-colors"
        data-testid="objectives-toggle"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
          Objectives {done}/{objectives.length}
        </span>
        <ChevronDown size={12} className={`text-cyan-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-1 w-72 bg-[#0A0C10]/70 backdrop-blur-md border border-white/10 shadow-[0_12px_30px_rgba(0,0,0,0.35)] max-h-[calc(100vh-6.25rem)] overflow-y-auto"
          data-testid="objectives-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Mission</div>
              <div className="text-sm font-semibold uppercase tracking-wide text-white">Objectives</div>
            </div>
            <div className="text-[10px] font-mono text-cyan-400">{done}/{objectives.length}</div>
          </div>

          <div className="p-3 max-h-[45vh] overflow-y-auto">
            {objectives.map((o) => (
              <div key={o.id} className="flex items-start gap-2 py-1" data-testid={`obj-${o.id}`}>
                {o.done ? <CheckCircle2 size={14} className="text-[#00fa9a] mt-0.5 shrink-0" /> : <Circle size={14} className="text-slate-500 mt-0.5 shrink-0" />}
                <span className={`text-xs font-mono leading-tight ${o.done ? "text-slate-500 line-through" : "text-slate-200"}`}>{o.label}</span>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10 space-y-2">
            <button data-testid="btn-scan" onClick={discoverDeposits} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-white/20 bg-white/5 hover:bg-cyan-500/10 hover:border-cyan-400 hover:text-cyan-400 text-white">
              <ScanLine size={12} /> Survey Terrain
            </button>
            <button data-testid="btn-request-colonists" onClick={requestColonists} className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-[#D85C31]/60 bg-[#D85C31]/10 hover:bg-[#D85C31]/25 text-[#E8A37E]">
              <Rocket size={12} /> Request Colonists +4
            </button>
            <button data-testid="btn-cargo-bay" onClick={openCargo} className={`w-full flex items-center justify-center gap-2 px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest border ${cargoActive ? "border-cyan-400/70 bg-cyan-500/10 text-cyan-400 animate-pulse" : "border-[#E8A37E]/60 bg-[#E8A37E]/5 hover:bg-[#E8A37E]/15 text-[#E8A37E]"}`}>
              <Package size={12} /> Cargo Bay {cargoActive ? "· In Transit" : ""}
            </button>
            <button data-testid="btn-research" onClick={openResearch} className={`w-full flex items-center justify-center gap-2 px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest border ${researchActive ? "border-[#B042FF]/70 bg-[#B042FF]/10 text-[#B042FF] animate-pulse" : "border-[#B042FF]/60 bg-[#B042FF]/5 hover:bg-[#B042FF]/15 text-[#B042FF]"}`}>
              <FlaskConical size={12} /> Research {researchActive ? "· Active" : ""} {researchCompleted.length > 0 ? `(${researchCompleted.length})` : ""}
            </button>
            <div className="grid grid-cols-4 gap-1">
              <button data-testid="btn-save" onClick={() => save("manual")} className="flex items-center justify-center px-1 py-1.5 border border-white/20 bg-white/5 hover:border-cyan-400 hover:text-cyan-400 text-white"><Save size={11} /></button>
              <button data-testid="btn-load" onClick={() => load("manual")} className="flex items-center justify-center px-1 py-1.5 text-[10px] font-mono uppercase border border-white/20 bg-white/5 hover:border-cyan-400 hover:text-cyan-400 text-white">Load</button>
              <button data-testid="btn-reset" onClick={reset} className="flex items-center justify-center px-1 py-1.5 border border-white/20 bg-white/5 hover:border-[#ff3366] hover:text-[#ff3366] text-white"><RotateCw size={11} /></button>
              <button data-testid="btn-mute" onClick={toggleMute} className={`flex items-center justify-center px-1 py-1.5 border ${muted ? "border-[#ff3366] text-[#ff3366]" : "border-white/20 bg-white/5 hover:border-cyan-400 hover:text-cyan-400 text-white"}`}>{muted ? <VolumeX size={11} /> : <Volume2 size={11} />}</button>
            </div>
            <div className="text-[10px] font-mono text-slate-400 pt-1 border-t border-white/5">Colonists: <span className="text-white">{colonists}</span> · Survived: <span className="text-[#00fa9a]">{survivedSols}/5 sols</span></div>
          </div>

          {victory && (
            <div className="absolute inset-0 -inset-x-[220px] flex items-center justify-center pointer-events-none">
              <div className="bg-[#0A0C10]/95 border border-[#00fa9a] p-6 flex flex-col items-center gap-3 pointer-events-auto" data-testid="victory-modal">
                <Trophy size={40} className="text-[#00fa9a]" />
                <div className="text-2xl font-bold uppercase tracking-wider text-white">Colony Secured</div>
                <button onClick={reset} data-testid="btn-victory-restart" className="mt-2 px-4 py-2 border border-[#00fa9a] text-[#00fa9a] hover:bg-[#00fa9a]/10 text-xs font-mono uppercase tracking-widest">Start New Colony</button>
              </div>
            </div>
          )}
          {defeat && (
            <div className="absolute inset-0 -inset-x-[220px] flex items-center justify-center pointer-events-none">
              <div className="bg-[#0A0C10]/95 border border-[#ff3366] p-6 flex flex-col items-center gap-3 pointer-events-auto" data-testid="defeat-modal">
                <Skull size={40} className="text-[#ff3366]" />
                <div className="text-2xl font-bold uppercase tracking-wider text-white">Colony Lost</div>
                <button onClick={reset} data-testid="btn-defeat-restart" className="mt-2 px-4 py-2 border border-[#ff3366] text-[#ff3366] hover:bg-[#ff3366]/10 text-xs font-mono uppercase tracking-widest">Try Again</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
